import { useState, useEffect } from "react";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import IconButton from "@mui/material/IconButton";
import ClearIcon from "@mui/icons-material/Clear";
import { motion } from "framer-motion";

import { setExpressLabel } from "../../../../GlobalFunctions/setExpressLabel";
import { db, doc, updateDoc, app } from "../../../../firebaseConfig";

import { useAtom } from "jotai";
import { themeAtom, isAdminAtom } from "../../../../-Atoms";

const DueDatesContainer = ({ jobData }) => {
  const [theme] = useAtom(themeAtom);
  const [isAdmin] = useAtom(isAdminAtom);

  const [dueDate, setDueDate] = useState(null);
  const [express, setExpress] = useState(false);
  const [expressDueDate, setExpressDueDate] = useState(null);
  const [dueDatePickerOpen, setDueDatePickerOpen] = useState(false);
  const [expressDatePickerOpen, setExpressDatePickerOpen] = useState(false);

  useEffect(() => {
    setDueDate(jobData.dueDate);
    setExpress(jobData.express);
    setExpressDueDate(jobData.expressDueDate);
  }, [jobData]);

  const dueDateValue = dueDate ? dayjs(dueDate) : null;
  const expressDueDateValue = expressDueDate ? dayjs(expressDueDate) : null;

  const handleDueDateChange = (newValue) => {
    if (!isAdmin) return;
    try {
      let dateWithTime;
      if (newValue && newValue.isValid()) {
        if (
          newValue.hour() === 0 &&
          newValue.minute() === 0 &&
          newValue.second() === 0
        ) {
          dateWithTime = newValue.hour(17).minute(30);
        } else {
          dateWithTime = newValue;
        }
        setDueDate(dateWithTime.toISOString());
        updateDoc(doc(db, "JobData", jobData.id), { dueDate: dateWithTime.toISOString() });
      }
    } catch (e) {
      setDueDate(null);
      updateDoc(doc(db, "JobData", jobData.id), { dueDate: null });
    }
  };

  const clearDueDate = (e) => {
    e.stopPropagation();
    if (!isAdmin) return;
    setDueDate(null);
    updateDoc(doc(db, "JobData", jobData.id), { dueDate: null });
  };

  const handleExpressDueDateChange = (newValue) => {
    if (!isAdmin) return;
    let date = null;
    try {
      date = newValue.toISOString();
    } catch (e) {
      console.log(e);
    }
    setExpressDueDate(date);
    updateDoc(doc(db, "JobData", jobData.id), { expressDueDate: date || null })
      .catch((error) => {
        console.log(error);
      });
  };

  const clearExpressDueDate = (e) => {
    e.stopPropagation();
    if (!isAdmin) return;
    setExpressDueDate(null);
    updateDoc(doc(db, "JobData", jobData.id), { expressDueDate: null })
      .catch((error) => {
        console.log(error);
      });
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.3,
        ease: "easeInOut",
        staggerChildren: 0.05,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
        ease: [0.25, 0.1, 0.25, 1.0],
      },
    },
    hover: { scale: 1.02 },
    tap: { scale: 0.98 },
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <FormControl
        fullWidth
        sx={{
          marginTop: 2,
          position: "relative",
          "& .MuiInputLabel-root": {
            color:
              theme === "dark" ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.7)",
            fontWeight: 500,
            fontSize: "0.95rem",
            transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
          },
        }}
        variant="filled"
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            width: "100%",
            borderRadius: "10px",
            overflow: "hidden",
            backgroundColor:
              theme === "dark" ? "rgba(35,35,35,0.7)" : "rgba(235,235,235,0.9)",
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            boxShadow:
              theme === "dark"
                ? "0 2px 10px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.1)"
                : "0 2px 10px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.5)",
            padding: 2,
          }}
        >
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
              }}
            >
              <motion.div variants={itemVariants}>
                <Box sx={{ position: "relative" }}>
                  <Box
                    onClick={() => {
                      if (isAdmin) setDueDatePickerOpen(true);
                    }}
                    sx={{
                      cursor: isAdmin ? "pointer" : "default",
                      width: "100%",
                    }}
                  >
                    <DatePicker
                      label="Due Date"
                      format="DD/MM/YYYY"
                      value={dueDateValue}
                      onChange={handleDueDateChange}
                      open={dueDatePickerOpen}
                      onClose={() => setDueDatePickerOpen(false)}
                      slots={{
                        openPickerIcon: () => null,
                      }}
                      slotProps={{
                        textField: {
                          variant: "filled",
                          fullWidth: true,
                          error: dueDate ? false : true,
                          inputProps: { readOnly: true },
                          InputProps: {
                            disableUnderline: true,
                            sx: {
                              borderRadius: "8px",
                              backgroundColor:
                                theme === "dark"
                                  ? "rgba(45,45,45,0.7)"
                                  : "rgba(250,250,250,0.9)",
                              boxShadow:
                                theme === "dark"
                                  ? "0 2px 5px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.05)"
                                  : "0 2px 5px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.5)",
                              "&:hover": {
                                backgroundColor:
                                  theme === "dark"
                                    ? "rgba(55,55,55,0.8)"
                                    : "rgba(255,255,255,1)",
                                boxShadow:
                                  theme === "dark"
                                    ? "0 4px 12px rgba(0,0,0,0.25), 0 0 0 1px rgba(255,255,255,0.1), 0 0 0 3px rgba(100,100,255,0.3)"
                                    : "0 4px 12px rgba(0,0,0,0.1), 0 0 0 1px rgba(0,0,0,0.05), 0 0 0 3px rgba(100,100,255,0.3)",
                              },
                              "&.Mui-focused": {
                                boxShadow:
                                  theme === "dark"
                                    ? "0 4px 15px rgba(0,0,0,0.3), 0 0 0 1px rgba(255,255,255,0.1), 0 0 0 3px rgba(100,100,255,0.5)"
                                    : "0 4px 15px rgba(0,0,0,0.15), 0 0 0 1px rgba(0,0,0,0.05), 0 0 0 3px rgba(100,100,255,0.5)",
                              },
                            },
                          },
                        },
                      }}
                    />
                  </Box>
                  {dueDateValue && (
                    <IconButton
                      aria-label="clear date"
                      onClick={clearDueDate}
                      size="small"
                      sx={{
                        position: "absolute",
                        right: 8,
                        top: "50%",
                        transform: "translateY(-50%)",
                        color:
                          theme === "dark"
                            ? "rgba(255,255,255,0.7)"
                            : "rgba(0,0,0,0.5)",
                        "&:hover": {
                          backgroundColor:
                            theme === "dark"
                              ? "rgba(255,255,255,0.1)"
                              : "rgba(0,0,0,0.05)",
                        },
                      }}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              </motion.div>

              <motion.div variants={itemVariants}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    gap: 2,
                    p: 1,
                    borderRadius: "8px",
                    backgroundColor: express
                      ? theme === "dark"
                        ? "rgba(233, 30, 99, 0.15)"
                        : "rgba(233, 30, 99, 0.08)"
                      : "transparent",
                    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                  }}
                >
                  <FormGroup
                    sx={{
                      flex: "0 0 auto",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={express || false}
                          onChange={(e) => {
                            if (!isAdmin) return;
                            setExpressLabel(
                              app,
                              jobData.id,
                              e.target.checked
                            );
                            setExpress(e.target.checked);
                            updateDoc(doc(db, "JobData", jobData.id), { express: e.target.checked });
                          }}
                          sx={{
                            color:
                              theme === "dark"
                                ? "rgba(255,255,255,0.7)"
                                : "rgba(0,0,0,0.7)",
                            "&.Mui-checked": {
                              color: theme === "dark" ? "#90caf9" : "#1976d2",
                            },
                          }}
                        />
                      }
                      label="Express"
                      sx={{
                        "& .MuiFormControlLabel-label": {
                          color:
                            theme === "dark"
                              ? "rgba(255,255,255,0.9)"
                              : "rgba(0,0,0,0.9)",
                          fontWeight: 600,
                        },
                      }}
                    />
                  </FormGroup>

                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ position: "relative" }}>
                      <Box
                        onClick={() => {
                          if (express && isAdmin)
                            setExpressDatePickerOpen(true);
                        }}
                        sx={{
                          cursor: express && isAdmin ? "pointer" : "default",
                          width: "100%",
                          opacity: express ? 1 : 0.7,
                        }}
                      >
                        <DatePicker
                          label="Express Due Date"
                          value={expressDueDateValue}
                          format="DD/MM/YYYY"
                          onChange={handleExpressDueDateChange}
                          open={expressDatePickerOpen}
                          onClose={() => setExpressDatePickerOpen(false)}
                          slots={{
                            openPickerIcon: () => null,
                          }}
                          slotProps={{
                            textField: {
                              variant: "filled",
                              fullWidth: true,
                              inputProps: { readOnly: true },
                              InputProps: {
                                disableUnderline: true,
                                sx: {
                                  borderRadius: "8px",
                                  backgroundColor:
                                    theme === "dark"
                                      ? "rgba(45,45,45,0.7)"
                                      : "rgba(250,250,250,0.9)",
                                  boxShadow:
                                    theme === "dark"
                                      ? "0 2px 5px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.05)"
                                      : "0 2px 5px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.5)",
                                  "&:hover": {
                                    backgroundColor:
                                      theme === "dark"
                                        ? "rgba(55,55,55,0.8)"
                                        : "rgba(255,255,255,1)",
                                    boxShadow:
                                      theme === "dark"
                                        ? "0 4px 12px rgba(0,0,0,0.25), 0 0 0 1px rgba(255,255,255,0.1), 0 0 0 3px rgba(100,100,255,0.3)"
                                        : "0 4px 12px rgba(0,0,0,0.1), 0 0 0 1px rgba(0,0,0,0.05), 0 0 0 3px rgba(100,100,255,0.3)",
                                  },
                                  "&.Mui-focused": {
                                    boxShadow:
                                      theme === "dark"
                                        ? "0 4px 15px rgba(0,0,0,0.3), 0 0 0 1px rgba(255,255,255,0.1), 0 0 0 3px rgba(100,100,255,0.5)"
                                        : "0 4px 15px rgba(0,0,0,0.15), 0 0 0 1px rgba(0,0,0,0.05), 0 0 0 3px rgba(100,100,255,0.5)",
                                  },
                                },
                              },
                            },
                          }}
                        />
                      </Box>
                      {expressDueDateValue && express && (
                        <IconButton
                          aria-label="clear express date"
                          onClick={clearExpressDueDate}
                          size="small"
                          sx={{
                            position: "absolute",
                            right: 8,
                            top: "50%",
                            transform: "translateY(-50%)",
                            color:
                              theme === "dark"
                                ? "rgba(255,255,255,0.7)"
                                : "rgba(0,0,0,0.5)",
                            "&:hover": {
                              backgroundColor:
                                theme === "dark"
                                  ? "rgba(255,255,255,0.1)"
                                  : "rgba(0,0,0,0.05)",
                            },
                          }}
                        >
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  </Box>
                </Box>
              </motion.div>
            </Box>
          </motion.div>
        </Box>
      </FormControl>
    </LocalizationProvider>
  );
};

export default DueDatesContainer;
